import Box from '@mui/material/Box';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode, type CSSProperties } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import { useSectionFieldStyles as useStyles } from './SectionField.styles';
import { SectionFieldContent } from './SectionFieldContent';

export type SectionValue = {
	name: string;
	isPrimary?: boolean;
	isSecondary?: boolean;
};

export type Value =
	| string
	| string[]
	| SectionValue
	| SectionValue[]
	| Record<string, SectionValue[]>
	| boolean
	| null
	| undefined;

type SectionFieldPropsBase = {
	label: ReactNode;
	to?: string | null;
	link?: string | null;
	tooltip?: string | null;
	collapseAt?: Breakpoint;
	labelWidth?: CSSProperties['width'];
	hideLabel?: boolean;
	customSx?: {
		root?: SxProps<Theme>;
		label?: SxProps<Theme>;
		groupName?: SxProps<Theme>;
		value?: SxProps<Theme>;
	};
};

type WithValue = {
	value: Value;
	children?: never;
};

type WithChildren = {
	value?: never;
	children: ReactNode;
};

export type SectionFieldProps = SectionFieldPropsBase &
	(WithValue | WithChildren);

const isValueSet = (value: Value) => {
	if (value === undefined || value === null) {
		return false;
	}

	if (typeof value === 'string') {
		return value.trim() !== '';
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (typeof value === 'object' && value !== null) {
		return Object.keys(value).length > 0;
	}

	return true;
};

export const SectionField = ({
	label,
	value,
	to,
	link,
	tooltip,
	collapseAt,
	labelWidth,
	hideLabel = false,
	children,
	customSx
}: SectionFieldProps) => {
	const sx = useStyles(collapseAt, labelWidth);

	if (!isValueSet(value) && !children) return null;

	return (
		<Box sx={mergeSx(sx.fieldContainer, customSx?.root)}>
			{!hideLabel && (
				<Typography
					variant='body2'
					component='div'
					sx={mergeSx(sx.label, customSx?.label)}
				>
					{label}
				</Typography>
			)}

			<Box sx={sx.valueContainer}>
				{children ? (
					children
				) : (
					<SectionFieldContent
						value={value}
						to={to}
						link={link}
						tooltip={tooltip}
						customSx={customSx}
					/>
				)}
			</Box>
		</Box>
	);
};
